import React from "react";
import { createPortal } from "react-dom";
import "./or-modal.css";
import { useOrModal } from "./or-modal.hook";

export type OrModalVariant = "default" | "danger";
export type OrModalSize = "sm" | "md" | "lg" | "xl" | "full";
export type OrModalMobileBehavior = "bottom-sheet" | "full-screen";

export interface OrModalProps {
  ariaLabel?: string;
  bodyClassName?: string;
  children: React.ReactNode;
  className?: string;
  closeLabel?: string;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  headerClassName?: string;
  headerSlot?: React.ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  mobileBehavior?: OrModalMobileBehavior;
  onClose: () => void;
  open: boolean;
  restoreFocus?: boolean;
  showCloseButton?: boolean;
  size?: OrModalSize;
  title?: React.ReactNode;
  variant?: OrModalVariant;
}

function CloseIcon(): React.ReactElement {
  return (
    <svg aria-hidden="true" fill="none" height="20" viewBox="0 0 24 24" width="20">
      <line stroke="currentColor" strokeWidth="2" x1="6" x2="18" y1="6" y2="18" />
      <line stroke="currentColor" strokeWidth="2" x1="18" x2="6" y1="6" y2="18" />
    </svg>
  );
}

export function OrModal({
  ariaLabel,
  bodyClassName,
  children,
  className,
  closeLabel = "Cerrar modal",
  closeOnEscape = true,
  closeOnOverlayClick = true,
  description,
  footer,
  footerClassName,
  headerClassName,
  headerSlot,
  initialFocusRef,
  mobileBehavior = "bottom-sheet",
  onClose,
  open,
  restoreFocus = true,
  showCloseButton = true,
  size = "md",
  title,
  variant = "default",
}: OrModalProps): React.ReactElement | null {
  const reactId = React.useId();
  const titleId = `or-modal-title-${reactId.replace(/:/g, "")}`;
  const descriptionId = `or-modal-description-${reactId.replace(/:/g, "")}`;
  const { dialogClassName, dialogRef, handleDialogKeyDown, handleOverlayClick, headerCloseButtonRef, modalClassName } =
    useOrModal({
      className,
      closeOnEscape,
      closeOnOverlayClick,
      initialFocusRef,
      mobileBehavior,
      onClose,
      open,
      restoreFocus,
      size,
      variant,
    });

  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={modalClassName}>
      <div aria-hidden="true" className="or-modal__overlay" onClick={handleOverlayClick} />

      <div
        aria-describedby={description ? descriptionId : undefined}
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        aria-modal="true"
        className={dialogClassName}
        onKeyDown={handleDialogKeyDown}
        ref={dialogRef}
        role={variant === "danger" ? "alertdialog" : "dialog"}
        tabIndex={-1}
      >
        <header className={["or-modal__header", headerClassName ?? ""].filter(Boolean).join(" ")}>
          <div className="or-modal__header-copy">
            {title ? (
              <h3 className="or-modal__title" id={titleId}>
                {title}
              </h3>
            ) : null}
            {headerSlot ? <div className="or-modal__header-slot">{headerSlot}</div> : null}
          </div>

          {showCloseButton ? (
            <button
              aria-label={closeLabel}
              className="or-modal__close"
              onClick={onClose}
              ref={headerCloseButtonRef}
              type="button"
            >
              <CloseIcon />
            </button>
          ) : null}
        </header>

        <div className={["or-modal__body", bodyClassName ?? ""].filter(Boolean).join(" ")}>
          {description ? (
            <p className="or-modal__description" id={descriptionId}>
              {description}
            </p>
          ) : null}
          {children}
        </div>

        {footer ? (
          <footer className={["or-modal__footer", footerClassName ?? ""].filter(Boolean).join(" ")}>{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
